import { GetStaticPropsContext, InferGetStaticPropsType } from "next";
import { join } from "path";
import { renderToString } from "react-dom/server";
import { getServerState } from "react-instantsearch";

import LayoutPage from "~/components/layout/layout-page";
import ProductGrid from "~/components/products/product-grid";
import ProductGridSkeleton from "~/components/products/product-grid-skeleton";
import CategoryProvider from "~/contexts/category";
import { usePageTracking } from "~/contexts/page-trackings";
import { mapLocale } from "~/services/algolia-utils";
import Eva from "~/services/eva";
import Logger from "~/services/logger/logger";
import CrossCountry from "~/types/cross-country";
import CommonUtils from "~/utils/common-utils";
import Constants from "~/utils/constants";
import evaUtils from "~/utils/eva-utils";
import invariant from "~/utils/invariant";
import ssrUtils from "~/utils/ssr-utils";
import TrackingUtils from "~/utils/tracking-utils";

type Props = InferGetStaticPropsType<typeof getStaticProps>;

export default function ProductListingPage(props: Props) {
  usePageTracking("category", TrackingUtils.mapTrackCategoryPage(props));

  return (
    <LayoutPage {...props} navColor="gradient">
      <CategoryProvider category={props.page.category}>
        <ProductGrid
          components={props.page.components}
          breadcrumbs={props.breadcrumbs}
          navigation={props.navigation}
          changeHeaderLayout={props.page.changeHeaderLayout}
          backgroundColors={props.page.backgroundColors}
          setId={props.page.category.fields.productSet?.fields?.productSetId}
          listScope="plp"
          initialData={props.initialData}
          serverState={props.serverState}
        />
      </CategoryProvider>
    </LayoutPage>
  );
}

export async function getStaticPaths() {
  return {
    paths: [], // TODO: add frequent pages
    fallback: "blocking",
  };
}

export async function getStaticProps(context: GetStaticPropsContext) {
  if (context.locale === context.defaultLocale) {
    return {
      notFound: true,
    };
  }

  const categorySlug = (context.params?.category as string[]).join("/");

  const pageData = await ssrUtils.getServerSideProductListingPageData(context, context.draftMode, categorySlug);

  if (!pageData.page) {
    const previewData = await ssrUtils.getServerSideProductListingPreviewPageData(context, context.params?.editorial);

    if (previewData.page?.fallbackUrl) {
      if (previewData.page.fallbackUrl === "NOT_FOUND") {
        return {
          notFound: true,
          revalidate: Constants.DEFAULT_STATIC_PAGE_TIME,
        };
      }

      let destination = join("/", context.locale!, previewData.page.fallbackUrl, "/");

      return {
        redirect: {
          destination,
          permanent: false,
        },
      };
    } else {
      // The page could not be found on draft mode or it did not have a fallback url.

      return {
        notFound: true,
        revalidate: Constants.DEFAULT_STATIC_PAGE_TIME,
      };
    }
  }

  if (pageData.page.fallbackUrl) {
    Logger.instance.warn(
      `Detected fallback url for category page ${context.params?.editorial}: ${pageData.page.fallbackUrl}`
    );

    if (pageData.page.fallbackUrl === "NOT_FOUND") {
      return {
        notFound: true,
        revalidate: Constants.DEFAULT_STATIC_PAGE_TIME,
      };
    }

    let destination = join("/", context.locale!, pageData.page.fallbackUrl, "/");

    return {
      redirect: {
        destination,
        permanent: false,
      },
    };
  }

  const categoryId = pageData.page.category.fields.categoryId;
  const productSet = pageData.page.category.fields.productSet?.fields?.productSetId;

  const organizationUnitId = pageData.organizationUnitId;

  const isEvaProductListing =
    CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_DISABLE_ALGOLIA_PRODUCT_LISTING) || !categoryId;

  if (isEvaProductListing) {
    const eva = new Eva({ organizationUnitId, locale: context.locale });

    let initialData: Omit<EVA.Core.SearchProductsResponse, "Products"> & {
      Products?: CrossCountry.Product[];
    };

    const initialKey = ["productListing", categoryId, productSet, Eva.DEFAULT_CATEGORY_SORT];

    if (productSet !== undefined) {
      initialData = await evaUtils.getProductsBySetWithChildren(eva, productSet);
    } else if (categoryId) {
      initialData = await evaUtils.getProductsByCategoryWithChildren(eva, categoryId);
    } else {
      invariant(false, "Rendering category page without productSet or categoryId: " + JSON.stringify(context.params));
    }

    return {
      props: {
        ...pageData,
        initialKey: JSON.stringify(initialKey),
        initialData: JSON.stringify(initialData),
        serverState: null,
      },
      revalidate: Constants.DEFAULT_STATIC_PAGE_TIME,
    };
  } else {
    const algoliaIndexId = mapLocale(organizationUnitId, context.locale);

    const serverState = await getServerState(
      <ProductGridSkeleton
        algoliaIndexId={algoliaIndexId}
        categoryId={categoryId}
        components={pageData.page.components}
        listScope="plp"
      />,
      {
        renderToString,
      }
    );

    return {
      props: {
        ...pageData,
        serverState,
        initialData: null,
      },
      revalidate: Constants.DEFAULT_STATIC_PAGE_TIME,
    };
  }
}
